import { getFfmpegWasmUrl } from './encoding/ffmpeg-runtime';
import type { PreflightResult } from './types';
import { formatBytes } from '../../utils/format';

const megabyte = 1024 * 1024;
const ffmpegBaseWorkingSetBytes = 320 * megabyte;
const audioWorkingSetMultiplier = 5;
const memorySafetyRatio = 0.68;
const minimumAudioMemoryLimitBytes = 96 * megabyte;
const unknownMemoryAudioLimitBytes = 700 * megabyte;
const wasmProbeModule = new Uint8Array([0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00]);

let browserPreflightResult = preflightBrowserCapabilities();

export function preflightAudioFile(file: File): PreflightResult {
  browserPreflightResult = browserPreflightResult.ok ? browserPreflightResult : preflightBrowserCapabilities();
  if (!browserPreflightResult.ok) {
    return browserPreflightResult;
  }

  return preflightAudioMemory(file);
}

function preflightBrowserCapabilities(): PreflightResult {
  if (!canRunWasm()) {
    return failPreflight('This browser cannot run the in-browser video encoder because WebAssembly is unavailable. Try a current desktop browser.');
  }

  if (typeof Worker === 'undefined') {
    return failPreflight('This browser cannot run the video encoder because Web Workers are unavailable. Try a current desktop browser.');
  }

  if (typeof Blob === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function' || typeof fetch !== 'function') {
    return failPreflight('This browser is missing file features needed for local video generation. Try a current desktop browser.');
  }

  if (typeof HTMLCanvasElement === 'undefined' || typeof HTMLCanvasElement.prototype.toBlob !== 'function') {
    return failPreflight('This browser cannot prepare the title-card image for encoding. Try a current desktop browser.');
  }

  if (usesCompressedFfmpegRuntime() && typeof DecompressionStream === 'undefined') {
    return failPreflight('This browser cannot unpack the FFmpeg runtime used for video generation. Try a current desktop browser.');
  }

  return { ok: true };
}

function canRunWasm() {
  try {
    return typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function' && WebAssembly.validate(wasmProbeModule);
  } catch {
    return false;
  }
}

function usesCompressedFfmpegRuntime() {
  return getFfmpegWasmUrl().endsWith('.gz');
}

function preflightAudioMemory(file: File): PreflightResult {
  if (file.size <= 0) {
    return failPreflight('The selected audio file appears to be empty. Choose an audio file with playable content.');
  }

  const estimatedWorkingSet = estimateAudioWorkingSetBytes(file.size);
  const heapLimit = getJsHeapSizeLimitBytes();
  if (heapLimit && estimatedWorkingSet > heapLimit * memorySafetyRatio) {
    return audioMemoryFailure(file, getHeapBasedAudioLimitBytes(heapLimit));
  }

  const deviceMemory = getDeviceMemoryGb();
  if (!heapLimit && deviceMemory && file.size > getDeviceMemoryAudioLimitBytes(deviceMemory)) {
    return audioMemoryFailure(file, getDeviceMemoryAudioLimitBytes(deviceMemory));
  }

  if (!heapLimit && !deviceMemory && file.size > unknownMemoryAudioLimitBytes) {
    return audioMemoryFailure(file, unknownMemoryAudioLimitBytes);
  }

  return { ok: true };
}

function estimateAudioWorkingSetBytes(fileSize: number) {
  return ffmpegBaseWorkingSetBytes + fileSize * audioWorkingSetMultiplier;
}

function getHeapBasedAudioLimitBytes(heapLimit: number) {
  return Math.max(minimumAudioMemoryLimitBytes, (heapLimit * memorySafetyRatio - ffmpegBaseWorkingSetBytes) / audioWorkingSetMultiplier);
}

function getJsHeapSizeLimitBytes() {
  const heapLimit = performance.memory?.jsHeapSizeLimit;
  return typeof heapLimit === 'number' && Number.isFinite(heapLimit) && heapLimit > 0 ? heapLimit : null;
}

function getDeviceMemoryGb() {
  const deviceMemory = navigator.deviceMemory;
  return typeof deviceMemory === 'number' && Number.isFinite(deviceMemory) && deviceMemory > 0 ? deviceMemory : null;
}

function getDeviceMemoryAudioLimitBytes(deviceMemoryGb: number) {
  if (deviceMemoryGb <= 1) {
    return 90 * megabyte;
  }
  if (deviceMemoryGb <= 2) {
    return 150 * megabyte;
  }
  if (deviceMemoryGb <= 4) {
    return 320 * megabyte;
  }
  return unknownMemoryAudioLimitBytes;
}

function audioMemoryFailure(file: File, limitBytes: number): PreflightResult {
  const roundedLimit = Math.max(minimumAudioMemoryLimitBytes, limitBytes);
  return failPreflight(
    `This audio file is ${formatBytes(file.size)}, which may be too large for the memory available in this browser. Try a file under ${formatBytes(
      roundedLimit,
    )}, a shorter clip, or a desktop browser with more memory.`,
  );
}

function failPreflight(message: string): PreflightResult {
  return { ok: false, message };
}
